import { AVATAR_LIST } from "enefel/avatars";
import { AVATAR_DEFAULT_LIST } from "enefel/avatarsDefault";

export const FRENCH_MONTHS = [
  "janvier",
  "février",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "août",
  "septembre",
  "octobre",
  "novembre",
  "décembre",
] as const;

export const CHART_COLORS = {
  newUsers: "#4CAF50",
  inactiveUsers: "#FF5722",
  activeUsers: "#2196F3",
  totalUsers: "#9C27B0",
  retentionRate: "#FF9800",
  gamesPlayed: "#607D8B",
  // Couleurs pour les dépenses
  totalSpent: "#E91E63",
  uniqueBuyers: "#3F51B5",
  totalPurchases: "#009688",
  avgSpentPerBuyer: "#795548",
  avatarPurchases: "#FF5722",
  badgePurchases: "#4CAF50",
} as const;

// Définition de l'activité utilisateur
export const USER_ACTIVITY_THRESHOLDS = {
  ACTIVE_DAYS: 30, // Un utilisateur est considéré actif s'il s'est connecté dans les 30 derniers jours
  INACTIVE_DAYS: 90, // Un utilisateur est considéré inactif après 90 jours sans connexion
  GAME_THRESHOLD: 1, // Nombre minimum de parties pour être considéré comme joueur actif
} as const;

// Seuils pour segmenter les acheteurs
export const SPENDING_SEGMENTS = {
  BIG_SPENDERS: 1000, // Gros dépensiers : 1000+ teeth
  MEDIUM_SPENDERS: 500, // Dépensiers moyens : 500-999 teeth
  SMALL_SPENDERS: 100, // Petits dépensiers : 100-499 teeth
  NON_SPENDERS: 0, // Non-dépensiers : 0-99 teeth
} as const;

// Noms des items pour l'affichage
export const ITEM_NAMES = {
  // Badges
  ELVEN_UNION: "Badge Union Elfique",
  SLANN: "Badge Slann",
  SNOTLING: "Badge Snotling",
  IMPERIAL: "Badge Impérial",
  VAMPIRE: "Badge Vampire",
  // Avatars (générique)
  AVATAR_COMMON: "Avatar Commun",
  AVATAR_RARE: "Avatar Rare",
  AVATAR_UNIQUE: "Avatar Unique",
} as const;

// Conversion teeth vers euros
export const TEETH_TO_EURO_RATES = {
  10: 1, // 10 teeth = 1€
  55: 5, // 55 teeth = 5€
  115: 10, // 115 teeth = 10€
  180: 15, // 180 teeth = 15€
  250: 20, // 250 teeth = 20€
  390: 30, // 390 teeth = 30€
  540: 40, // 540 teeth = 40€
  1120: 80, // 1120 teeth = 80€
};

// Prix des produits en teeth
export const PRODUCT_TEETH = {
  1: 10,
  5: 55,
  10: 115,
  15: 180,
  20: 250,
  30: 390,
  40: 540,
  80: 1120,
};

// Prix des items en teeth
export const ITEM_PRICES = {
  // Avatars
  AVATAR_COMMON: 100, // 100 teeth ≈ 8.7€
  AVATAR_RARE: 250, // 250 teeth ≈ 20€
  AVATAR_UNIQUE: 390, // 390 teeth ≈ 30€

  // Badges
  BADGE: 180, // 180 teeth ≈ 15€
};

// Fonction pour convertir teeth en euros (approximatif)
export const teethToEuro = (teeth: number): number => {
  // Utilise le taux le plus proche pour une conversion approximative
  const rates = Object.entries(TEETH_TO_EURO_RATES);
  let bestRate = rates[0];

  for (const [teethAmount, euroAmount] of rates) {
    if (
      Math.abs(teeth - parseInt(teethAmount)) <
      Math.abs(teeth - parseInt(bestRate[0]))
    ) {
      bestRate = [teethAmount, euroAmount];
    }
  }

  const teethPerEuro = parseInt(bestRate[0]) / bestRate[1];
  return Math.round((teeth / teethPerEuro) * 100) / 100; // Arrondi à 2 décimales
};

// Fonction pour formater les montants en euros
export const formatEuro = (amount: number): string => {
  return `${amount.toFixed(2)}€`;
};

// Fonction pour obtenir le chemin de l'avatar
export const getAvatarImagePath = (itemKey: string): string => {
  const file = itemKey.replace(".jpeg", ".png");

  for (const avatarType of Object.keys(AVATAR_LIST)) {
    const avatars = AVATAR_LIST[avatarType as keyof typeof AVATAR_LIST];
    if (
      avatars &&
      avatars.some((avatar) => avatar.file.replace(".jpeg", ".png") === file)
    ) {
      return `${process.env.PUBLIC_URL}/tile/shop/avatar/${avatarType}/${itemKey}`;
    }
  }

  for (const avatarType of Object.keys(AVATAR_DEFAULT_LIST)) {
    const avatars =
      AVATAR_DEFAULT_LIST[avatarType as keyof typeof AVATAR_DEFAULT_LIST];
    if (
      avatars &&
      avatars.some((avatar) => avatar.file.replace(".jpeg", ".png") === file)
    ) {
      return `${process.env.PUBLIC_URL}/tile/shop/avatar-default/${avatarType}/${itemKey}`;
    }
  }

  return `${process.env.PUBLIC_URL}/tile/shop/avatar/${itemKey}`; // Fallback
};

// Fonction pour obtenir le nom d'un item
export const getItemName = (itemKey: string, type: string): string => {
  if (type === "a") {
    // Avatar
    for (const avatarType of Object.keys(AVATAR_LIST)) {
      const avatars = AVATAR_LIST[avatarType as keyof typeof AVATAR_LIST];
      if (avatars && avatars.some((avatar) => avatar.file === itemKey)) {
        return `Avatar ${avatarType}`;
      }
    }
    for (const avatarType of Object.keys(AVATAR_DEFAULT_LIST)) {
      const avatars =
        AVATAR_DEFAULT_LIST[avatarType as keyof typeof AVATAR_DEFAULT_LIST];
      if (avatars && avatars.some((avatar) => avatar.file === itemKey)) {
        return `Avatar ${avatarType} (défaut)`;
      }
    }
    const cleanedKey = itemKey.replace(".jpeg", "").replace(".png", "");
    return `Avatar ${cleanedKey.substring(0, 8)}`;
  } else if (type === "b") {
    // Badge
    return `Badge ${itemKey}`;
  }
  return itemKey;
};
